import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table, Select, Button, Space, Typography, Alert, Tag, Spin, Tooltip, Tabs,
  Modal, Upload, Radio, message, Input,
} from 'antd';
import {
  SaveOutlined, ReloadOutlined, LockOutlined, UnlockOutlined,
  DownloadOutlined, UploadOutlined, InfoCircleOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';


import { getPolicies } from '../../actions/userActions';
import { SIDEBAR_NAV_ITEMS } from '../../config/navPolicyCatalog';
import { getPagePolicyRoutes, getSettingsCardRoutes } from '../../utils/routeRegistry';
import {
  EMPTY_NAV_POLICY_CONFIG,
  clearNavPolicyConfigCache,
  normalizeNavPolicyConfig,
  publishNavPolicyConfig,
  refreshNavPolicyConfig,
  buildNavPolicyExportPayload,
  saveNavPolicyConfig,
  applyNavPolicyImport,
  loadNavPolicyConfig,
} from '../../utils/navPolicyConfig';
import { readJsonFile } from '../../utils/readJsonFile';

const { Text } = Typography;

const filterNavPolicyItems = (items, searchTerm) => {
  const query = searchTerm.trim().toLowerCase();
  if (!query) return items;

  return items.filter((item) =>
    item.label?.toLowerCase().includes(query)
    || item.key?.toLowerCase().includes(query)
    || item.secondary?.toLowerCase().includes(query),
  );
};

const NavPolicyManager = ({ drawerHeight }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { policies = [] } = useSelector((s) => s.policies || {});
  const [config, setConfig] = useState(() => ({ ...EMPTY_NAV_POLICY_CONFIG }));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('sidebar');
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [importMode, setImportMode] = useState('Merge');
  const [importFile, setImportFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const pageItems = useMemo(() => getPagePolicyRoutes(), []);
  const cardItems = useMemo(() => getSettingsCardRoutes(), []);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      clearNavPolicyConfigCache();
      const normalized = await loadNavPolicyConfig();
      setConfig(normalized);
    } catch {
      setError(t('Failed to load nav policy config'));
      setConfig({ ...EMPTY_NAV_POLICY_CONFIG });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (policies.length === 0) {
      dispatch(getPolicies());
    }
    loadConfig();
  }, [dispatch, loadConfig, policies.length]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveNavPolicyConfig(config);
      publishNavPolicyConfig(config);
      setHasChanges(false);
      message.success(t('Nav policy config saved'));
    } catch (err) {
      const msg = err?.message || t('Failed to save nav policy config');
      setError(msg);
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    await refreshNavPolicyConfig();
    await loadConfig();
    setHasChanges(false);
  };

  const handleExport = () => {
    try {
      const payload = buildNavPolicyExportPayload(config);
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nav-policy-config-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      message.success(t('Exported successfully'));
    } catch {
      message.error(t('Export failed'));
    }
  };

  const resetImportModal = () => {
    setIsImportModalVisible(false);
    setImportFile(null);
    setImportMode('Merge');
  };

  const handleImport = async () => {
    try {
      if (!importFile) {
        message.error(t('Please select a JSON file'));
        return;
      }
      const importData = await readJsonFile(importFile);
      setConfig(applyNavPolicyImport(config, importData, importMode));
      setHasChanges(true);
      resetImportModal();
      message.success(t('Import applied — review and save to persist'));
    } catch (e) {
      message.error(e instanceof SyntaxError ? t('Invalid JSON file') : t('Import failed'));
    }
  };

  const handlePolicyChange = (section, itemKey, values) => {
    setConfig((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [itemKey]: values,
      },
    }));
    setHasChanges(true);
  };

  const policyOptions = policies.map((p) => ({ value: p, label: p }));

  const buildColumns = (section, itemLabel, secondaryKey) => [
    {
      title: itemLabel,
      dataIndex: 'label',
      width: 180,
      render: (label, row) => (
        <Space direction="vertical" size={0}>
          <Space>
            {config[section]?.[row.key]?.length > 0
              ? <LockOutlined style={{ color: '#faad14' }} />
              : <UnlockOutlined style={{ color: '#52c41a' }} />}
            <Text strong>{label}</Text>
          </Space>
          {secondaryKey && (
            <Text type="secondary" style={{ fontSize: 12 }}>{row[secondaryKey]}</Text>
          )}
        </Space>
      ),
    },
    {
      title: t('Required Policy / Policies'),
      dataIndex: 'key',
      render: (itemKey) => (
        <Select
          mode="multiple"
          allowClear
          showSearch
          placeholder={t('No restriction (visible to all logged-in users)')}
          value={config[section]?.[itemKey] || []}
          onChange={(values) => handlePolicyChange(section, itemKey, values)}
          options={policyOptions}
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
          maxTagCount="responsive"
        />
      ),
    },
    {
      title: t('Status'),
      dataIndex: 'key',
      width: 120,
      render: (itemKey) => {
        const perms = config[section]?.[itemKey] || [];
        if (perms.length === 0) {
          return <Tag color="green">{t('Open')}</Tag>;
        }
        return (
          <Tooltip title={perms.join(', ')}>
            <Tag color="orange" icon={<LockOutlined />}>
              {perms.length} {perms.length === 1 ? t('policy') : t('policies')}
            </Tag>
          </Tooltip>
        );
      },
    },
  ];

  const sidebarData = useMemo(() => SIDEBAR_NAV_ITEMS.map((item) => ({
    ...item,
    secondary: item.path,
  })), []);

  const cardData = useMemo(() => cardItems.map((item) => ({
    key: item.path,
    label: item.label,
    secondary: item.path,
  })), [cardItems]);

  const pageData = useMemo(() => pageItems.map((route) => ({
    key: route.path,
    label: route.label,
    secondary: route.path,
  })), [pageItems]);

  const filteredSidebarData = useMemo(
    () => filterNavPolicyItems(sidebarData, searchTerm),
    [sidebarData, searchTerm],
  );
  const filteredCardData = useMemo(
    () => filterNavPolicyItems(cardData, searchTerm),
    [cardData, searchTerm],
  );
  const filteredPageData = useMemo(
    () => filterNavPolicyItems(pageData, searchTerm),
    [pageData, searchTerm],
  );

  const tabItems = [
    {
      key: 'sidebar',
      label: t('Sidebar Nav Items'),
      children: (
        <Table
          dataSource={filteredSidebarData}
          columns={buildColumns('sidebar', t('Nav Item'), 'secondary')}
          rowKey="key"
          pagination={false}
          size="small"
        />
      ),
    },
    {
      key: 'cards',
      label: t('Control Panel Cards'),
      children: (
        <Table
          dataSource={filteredCardData}
          columns={buildColumns('cards', t('Card'), 'secondary')}
          rowKey="key"
          pagination={false}
          size="small"
        />
      ),
    },
    {
      key: 'pages',
      label: t('Pages'),
      children: (
        <Table
          dataSource={filteredPageData}
          columns={buildColumns('pages', t('Page'), 'secondary')}
          rowKey="key"
          pagination={false}
          size="small"
        />
      ),
    },
  ];

  const actionBar = (
    <Space wrap>
      <Button icon={<DownloadOutlined />} onClick={handleExport} size="small">
        {t('Export')}
      </Button>
      <Button icon={<UploadOutlined />} onClick={() => setIsImportModalVisible(true)} size="small">
        {t('Import')}
      </Button>
      <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading} size="small">
        {t('Refresh')}
      </Button>
      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={saving}
        disabled={!hasChanges}
        size="small"
      >
        {t('Save')}
      </Button>
    </Space>
  );

  return (
    <div style={{ height: drawerHeight || '90vh', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexShrink: 0 }}>
        {hasChanges && <Tag color="warning">{t('Unsaved changes')}</Tag>}
        <div style={{ marginLeft: 'auto' }}>{actionBar}</div>
      </div>

      <div className="nav-policy-drawer-content" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setError(null)}
          />
        )}

        <Spin spinning={loading} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Input.Search
            allowClear
            placeholder={t('Search by name or path')}
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: 12, maxWidth: 420 }}
            size="small"
          />
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            size="small"
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
            className="nav-policy-drawer-tabs"
          />
        </Spin>

        <Modal
          title={t('Import Nav Policy Config')}
          open={isImportModalVisible}
          onOk={handleImport}
          onCancel={resetImportModal}
          okText={t('Import')}
          okButtonProps={{ disabled: !importFile }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              message={
                <>
                  <Text strong>{t('Merge')}</Text> {t('updates matching keys without removing others.')}{' '}
                  <Text strong>{t('Override')}</Text> {t('replaces the entire nav policy config.')}
                  {' '}{t('Save after import to persist.')}
                </>
              }
            />

            <div>
              <Text type="secondary">{t('Import mode')}</Text>
              <br />
              <Radio.Group
                value={importMode}
                onChange={(e) => setImportMode(e.target.value)}
                style={{ marginTop: 8 }}
              >
                <Radio value="Merge">{t('Merge')}</Radio>
                <Radio value="Override">{t('Override')}</Radio>
              </Radio.Group>
            </div>

            <Upload
              accept=".json"
              maxCount={1}
              beforeUpload={(file) => {
                setImportFile(file);
                return false;
              }}
              onRemove={() => setImportFile(null)}
              fileList={importFile ? [{ uid: '1', name: importFile.name, status: 'done' }] : []}
            >
              <Button icon={<UploadOutlined />}>{t('Select JSON file')}</Button>
            </Upload>
          </Space>
        </Modal>
      </div>
    </div>
  );
};

export default NavPolicyManager;
